import { useRef } from 'react'

import { DateRange } from '@factorialco/f0-react/dist/experimental'

import { useOne } from 'modules/ai/lib/useOne'
import { deriveFiltersFromDateFilter } from 'modules/ai_reports/components/DatasourceTypeRenderer'
import { ReportSchemaType, ReportType } from 'modules/ai_reports/lib/types'
import { AiReportsResult } from 'modules/ai_reports/lib/useCreateAnalyticsReportActionResolver'

import { useReportActions } from './useReportActions'
import { useReportCoreState } from './useReportCoreState'
import { useReportFiltersSync } from './useReportFiltersSync'

/**
 * Converts filter objects with string dates to Date instances.
 * Used when loading reports from the backend.
 */
function filtersToDates(filters: {
  snapshotDate?: string | null
  dateRange?: { from: string; to: string } | null
}): AiReportsResult['filters'] {
  const result: AiReportsResult['filters'] = {}

  if (filters && filters.snapshotDate) {
    result.snapshotDate = filters.snapshotDate
  }

  if (filters && filters.dateRange) {
    result.dateRange = {
      from: filters.dateRange.from,
      to: filters.dateRange.to,
    }
  }

  return result
}

/**
 * Extracts the initial date filter value from a report's filters.
 * Handles both date range and snapshot date formats.
 */
export function getInitialDateFilter(reportFilters: ReportType['filters']): DateRange | undefined {
  if (reportFilters?.dateRange) {
    return {
      from: new Date(reportFilters.dateRange.from),
      to: new Date(reportFilters.dateRange.to),
    }
  }

  if (reportFilters?.snapshotDate) {
    return {
      from: new Date(reportFilters.snapshotDate),
      to: undefined,
    }
  }

  return undefined
}

/**
 * Creates the date navigator config argument from report filters.
 * Used for initializing the date navigator component.
 */
export function getDateNavigatorConfigArg(reportFilters: ReportType['filters']): {
  dateRange?: { from: Date; to: Date }
  snapshotDate?: Date
} {
  if (reportFilters?.dateRange) {
    return {
      dateRange: {
        from: new Date(reportFilters.dateRange.from),
        to: new Date(reportFilters.dateRange.to),
      },
    }
  }

  if (reportFilters?.snapshotDate) {
    return { snapshotDate: new Date(reportFilters.snapshotDate) }
  }

  return {}
}

type UseReportStateOptions = {
  datasourceId: string
  initialReport?: ReportType
  initialSqlQuery?: string
  initialDescription?: string
  initialDateFilter?: DateRange
  initialFilters?: ReportType['filters']
  initialColumnFormatHints?: AiReportsResult['sqlAnswer']['column_format_hints']
  reportName?: string
  datasourceName?: string
  schema: ReportSchemaType
  onSaveSuccess?: (reportId: string) => void
}

/**
 * Main hook that composes core state, filter synchronization, and actions.
 */
export function useReportState({
  datasourceId,
  initialReport,
  initialSqlQuery,
  initialDescription,
  initialDateFilter,
  initialFilters,
  initialColumnFormatHints = [],
  reportName,
  schema,
  onSaveSuccess,
}: UseReportStateOptions) {
  const { state, dispatch, NavConfirmModal, setNavigationBlocked } = useReportCoreState({
    datasourceId,
    initialReport,
    initialSqlQuery,
    initialDescription,
    initialDateFilter,
    initialFilters,
    initialColumnFormatHints,
  })

  // `useOne` watches `coCreationData` by reference in a useEffect dep array and
  // calls setState() on any ref change. If we recreate this object on every
  // render it triggers a tight re-render/fetch loop across the whole report
  // page. Capture it once on mount via a ref so the reference stays stable for
  // the entire lifetime of the hook.
  const initialAgentStateRef = useRef<AiReportsResult | null>(null)
  if (initialAgentStateRef.current === null) {
    initialAgentStateRef.current = {
      type: 'text2sql',
      currentDataset: datasourceId,
      sqlAnswer: {
        sql: state.sqlQuery,
        description: state.description,
        column_format_hints: initialColumnFormatHints,
        title: initialReport?.name || '',
        summary: '',
        query_summary: initialReport?.description || '',
        tables: [],
        resolved_entities_info: [],
        success: false,
        synthetic_sql: false,
      },
      // For EXISTING reports: initialFilters contains all filters from backend including
      // jsonLogicFilters, so we use it directly.
      // For NEW reports: initialFilters is undefined, but initialDateFilter may be set
      // from the dateNavigatorConfig default value. Convert it to agent state format.
      filters: initialFilters
        ? {
            ...filtersToDates(initialFilters),
            jsonLogicFilters: initialFilters.jsonLogicFilters,
          }
        : initialDateFilter
          ? deriveFiltersFromDateFilter(initialDateFilter, datasourceId)
          : undefined,
    }
  }

  const { coCreationData: agentState, setCoCreationData: setAgentState } = useOne<AiReportsResult>(
    ['reports'],
    initialAgentStateRef.current
  )

  useReportFiltersSync({
    state,
    schema,
    dispatch,
    agentState,
    setAgentState,
    datasourceId,
  })

  const { saveReport, handleExport } = useReportActions({
    state,
    schema,
    dispatch,
    agentState,
    datasourceId,
    initialReport,
    reportName,
    onSaveSuccess,
    setNavigationBlocked,
  })

  return {
    state,
    dispatch,
    agentState,
    NavConfirmModal,
    saveReport,
    handleExport,
  }
}
